// @ts-check
/* eslint-disable no-return-assign, react/jsx-no-literals, react/jsx-no-useless-fragment, react/jsx-one-expression-per-line, sort-imports */
import React, {useEffect, useMemo} from "react"
import {Pressable, StyleSheet, View} from "react-native"
import ConfigReader from "./config-reader"
import EditPage from "./edit-page"
import FlashNotifications from "flash-notifications/build/flash-notifications.js"
import hasEditConfig from "./has-edit-config.js"
import IndexPage from "./index-page"
import Layout from "./layout/index"
import Link from "../link"
import memo from "set-state-compare/build/memo.js"
// @ts-expect-error
import * as models from "models.js" // eslint-disable-line import/no-unresolved
import Params from "../params.js"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import ShowPage from "./show-page/index"
import ShowReflectionActions from "./show-reflection-actions"
import ShowReflectionPage from "./show-reflection-page"
import Text from "../utils/text"
import useCanCan from "../use-can-can.js"
import useCurrentUser from "../use-current-user.js"
import useQueryParams from "on-location-changed/build/use-query-params.js"
import useStyles from "../use-styles.js"

const styles = StyleSheet.create({
  actionsView: {
    flexDirection: "row",
    alignItems: "center"
  },
  createNewModelLink: {
    marginLeft: 10,
    marginRight: 10
  },
  destroyModelLink: {
    marginLeft: 10,
    marginRight: 10
  },
  editModelLink: {
    marginLeft: 10,
    marginRight: 10
  }
})

const dataSets = {}

/** @typedef {Record<string, never>} Props */
/**
 * @typedef {object} State
 * @property {object | undefined} model
 */
export default memo(shapeComponent(/** @augments {ShapeComponent<Props, State>} */ class ApiMakerSuperAdmin extends ShapeComponent {
  loadModelRequestId = 0
  state = {
    model: undefined
  }

  setup() {
    this.queryParams = useQueryParams()

    if (this.queryParams.model) {
      this.modelClass = models[this.queryParams.model]
    } else {
      this.modelClass = null
    }

    this.configReader = useMemo(() => this.modelClass && ConfigReader.forModel(this.modelClass), [this.modelClass])
    this.modelId = this.queryParams.model_id
    this.modelName = this.modelClass?.modelClassData()?.name
    this.pageToShow = this.currentPageToShow()
    const currentUserData = useCurrentUser({withData: true})

    this.currentUser = currentUserData.model

    this.canCan = useCanCan(
      () => {
        const abilities = []

        if (this.modelClass) abilities.push([this.modelClass, ["new"]])

        return abilities
      },
      [currentUserData.model?.primaryKey?.(), this.modelClass]
    )

    useEffect(() => {
      if (this.tt.pageNeedsLoadedModel()) {
        this.loadModel()
      } else {
        this.setState({model: undefined})
      }

      return () => {
        this.loadModelRequestId += 1
      }
    }, [this.modelClass, this.modelId, this.pageToShow])
  }

  loadModel = async () => {
    // Show/edit pages should only accept the newest model load after route changes.
    const requestId = this.loadModelRequestId + 1

    this.loadModelRequestId = requestId
    const {configReader, modelClass, modelId, modelName} = this.tt

    if (modelId && modelClass) {
      const abilities = {}
      const abilitiesForModel = ["destroy", "edit"]
      const layoutSelect = configReader?.modelConfig?.layout?.select

      abilities[modelName] = abilitiesForModel

      const query = await modelClass
        .ransack({id_eq: modelId})
        .abilities(abilities)

      if (layoutSelect) query.select(layoutSelect)

      const model = await query.first()

      if (requestId != this.loadModelRequestId) return

      this.setState({model})
    } else {
      if (requestId != this.loadModelRequestId) return

      this.setState({model: undefined})
    }
  }

  render() {
    const {canCan, configReader, modelClass, modelId, modelName, pageToShow, queryParams} = this.tt
    const {model} = this.s
    const modelConfigActions = configReader?.modelConfig?.actions

    const actionsViewStyle = useStyles(styles, "actionsView")

    const actions = useMemo(
      () => <View style={actionsViewStyle}>
        {model && modelConfigActions && modelConfigActions({model})}
        {modelClass && pageToShow == "index" &&
          <>
            {canCan?.can("new", modelClass) && hasEditConfig(modelClass) &&
              <Link
                dataSet={dataSets.createNewModelLink ||= {class: "create-new-model-link"}}
                style={styles.createNewModelLink}
                to={Params.withParams({model: modelName, mode: "new"})}
              >
                <Text>Create new</Text>
              </Link>
            }
          </>
        }
        {model && pageToShow == "show" &&
          <>
            {model.can("edit") && hasEditConfig(modelClass) &&
              <Link
                dataSet={dataSets.editModelLink ||= {class: "edit-model-link"}}
                style={styles.editModelLink}
                to={Params.withParams({model: modelName, model_id: modelId, mode: "edit"})}
              >
                <Text>Edit</Text>
              </Link>
            }
            {model.can("destroy") &&
              <Pressable
                dataSet={dataSets.destroyModelLink ||= {class: "destroy-model-link"}}
                onPress={this.tt.onDestroyClicked}
                style={styles.destroyModelLink}
              >
                <Text>Delete</Text>
              </Pressable>
            }
          </>
        }
        {pageToShow == "show_reflection" &&
          <ShowReflectionActions model={model} modelClass={modelClass} reflectionName={queryParams.model_reflection} />
        }
      </View>, // eslint-disable-line react/jsx-closing-tag-location
      [canCan.getCacheKey(), configReader?.modelConfig?.actions, model, modelClass, pageToShow]
    )

    return (
      <Layout actions={actions} active={queryParams.model} headerTitle={modelClass?.modelName()?.human({count: 2})}>
        {pageToShow == "index" &&
          <IndexPage
            key={`index-page-${modelName}`}
            modelClass={modelClass}
          />
        }
        {pageToShow == "show" &&
          <ShowPage
            key={`show-page-${modelName}-${modelId}`}
            modelClass={modelClass}
            modelId={modelId}
          />
        }
        {pageToShow == "show_reflection" &&
          <ShowReflectionPage
            key={`show-reflection-page-${modelName}-${modelId}`}
            modelClass={modelClass}
            modelId={modelId}
          />
        }
        {pageToShow == "edit" &&
          <EditPage
            key={`edit-page-${modelName}-${modelId}`}
            modelClass={modelClass}
            modelId={modelId}
          />
        }
      </Layout>
    )
  }

  onDestroyClicked = async () => {
    // eslint-disable-next-line no-alert
    if (!confirm("Are you sure?")) {
      return
    }

    try {
      await this.s.model.destroy()

      Params.changeParams({mode: undefined, model_id: undefined})
    } catch (error) {
      FlashNotifications.errorResponse(error)
    }
  }

  currentPageToShow = () => {
    const {queryParams} = this.tt

    if (queryParams.model && queryParams.model_id && queryParams.model_reflection) {
      return "show_reflection"
    } else if (queryParams.model && queryParams.model_id && queryParams.mode == "edit") {
      return "edit"
    } else if (queryParams.model && queryParams.model_id) {
      return "show"
    } else if (queryParams.model && queryParams.mode == "new") {
      return "edit"
    } else if (queryParams.model) {
      return "index"
    }

    return "welcome"
  }

  pageNeedsLoadedModel = () => {
    const {pageToShow} = this.tt

    return pageToShow == "show" || pageToShow == "show_reflection"
  }
}))
